const tryCreateURL = (urlString: string) => {
  try {
    return new URL(urlString);
  } catch {
    return undefined;
  }
};

const uncPathRegex = /^\\\\[^\\/]+\\[^\\/]/;

export const isUncPath = (value: string): boolean => uncPathRegex.test(value.trim());

export const isFileUrl = (url: URL): boolean => url.protocol === "file:";

export const getUrlHostSafe = (url: string): string => tryCreateURL(url)?.host ?? url;

export const tryGetWebUrlWithProtocol = (value: string): URL | undefined => {
  const sanitizedValue = value.trim();
  if (!sanitizedValue || isUncPath(sanitizedValue)) {
    return undefined;
  }

  const urlWithProtocol = /^[a-z]+:\/\//i.test(sanitizedValue) ? sanitizedValue : `https://${sanitizedValue}`;
  const url = tryCreateURL(urlWithProtocol);
  return url && url.hostname.includes(".") && ["http:", "https:"].includes(url.protocol) ? url : undefined;
};

// Accepts UNC paths ("\\server\share\folder") and file: URLs, normalizing both to a file: URL.
export const tryGetFileUrl = (value: string): URL | undefined => {
  const sanitizedValue = value.trim();
  if (isUncPath(sanitizedValue)) {
    return tryCreateURL(`file:${sanitizedValue.replace(/\\/g, "/")}`);
  }

  if (!/^file:/i.test(sanitizedValue)) {
    return undefined;
  }

  const url = tryCreateURL(sanitizedValue);
  return url && isFileUrl(url) && url.pathname !== "/" ? url : undefined;
};

export const tryGetLinkUrl = (value: string): URL | undefined =>
  tryGetFileUrl(value) ?? tryGetWebUrlWithProtocol(value);

// Converts a file: URL back to the Windows path a user can paste into File Explorer.
export const getFileSystemPath = (url: URL): string => {
  const path = decodeURIComponent(url.pathname).replace(/\//g, "\\");
  return url.host ? `\\\\${url.host}${path}` : path.replace(/^\\/, "");
};

export const getSameOriginUrl = (origin: string, path: string): string | undefined => {
  const url = tryCreateURL(`${origin}${path}`);
  return url?.origin === origin ? url.href : undefined;
};

export const normalizeLinkUrl = (value: string): string => tryGetLinkUrl(value)?.href ?? value;

// Parses a markdown link "[title](url)". Legacy values are stored as raw URLs, so fall back to treating the whole value as both title and URL.
export const parseUrlFromMarkdown = (markdown: string): { title: string; url: URL | undefined } => {
  const match = markdown.match(/^\[(.+)\]\((.+)\)$/);
  const title = match?.[1] || markdown;
  const url = tryGetLinkUrl(match?.[2] || markdown);
  return { title, url };
};
